/**
 * Schémas Zod du module Parcellaire (Lot B.5) — partagés API ⇄ ERP.
 *
 * MODÈLE PCV + SPCV (migration 0033, refonte 2026-05-21) :
 *   - PCV (Parcelle Cadastrale Viticole) : unité cadastrale (commune INSEE +
 *     section + numéro). Attributs cadastraux uniquement.
 *   - SPCV (Sous-Parcelle Viticole) : unité agronomique homogène
 *     dans une PCV. Mono-cépage par définition. Une PCV contient N SPCV.
 *
 * Endpoints couverts :
 *   - PCV : creer / modifier / supprimer / getById / lister
 *   - SPCV : creerSousParcelle / modifierSousParcelle / supprimerSousParcelle
 *
 * Source des règles métier : `docs/features/vignoble-terrain/00-plan.md`
 * + CdC AOC Champagne arrêté 31/07/2025.
 *
 * Convention :
 *  - `surface_ares` stocké en ARES (1 ha = 100 ares), précision 0,01.
 *  - `commune_insee` 5 caractères COG (chiffres ou 2A/2B Corse).
 *  - Référence cadastrale : section `^[A-Z0-9]{1,4}$`, numéro `^[0-9]{1,4}[A-Z]{0,2}$`.
 */

import { z } from "zod";

import { cruSchema, choixCruParcelleSchema } from "./parcellaire-cru.js";
import { faireValoirParcelleSchema } from "./parcellaire-faire-valoir.js";
import { anneeReferenceVignobleSchema } from "./parcellaire-stades.js";
export * from "./parcellaire-faire-valoir.js";
import { isValidCommuneInsee } from "./validation.js";
export { cruSchema, type Cru } from "./parcellaire-cru.js";

// ────────────────────────────────────────────────────────────────────────────
// Helpers de validation cadastre
// ────────────────────────────────────────────────────────────────────────────

/** Code INSEE commune (5 chars, chiffres ou 2A/2B Corse). */
export function validateCommuneInsee(value: string): boolean {
  return isValidCommuneInsee(value);
}

/**
 * Section cadastrale : 1 à 4 caractères alphanumériques majuscules
 * (ex : « AB », « 01 », « ZA », « ZA01 »). Convention DGFiP.
 */
export function validateSection(value: string): boolean {
  return /^[A-Z0-9]{1,4}$/.test(value);
}

/**
 * Numéro de parcelle cadastrale : 1 à 4 chiffres, suffixe alpha optionnel
 * de 1 à 2 lettres post-remembrement (ex : « 0123 », « 123A », « 12AB »).
 */
export function validateNumero(value: string): boolean {
  return /^[0-9]{1,4}[A-Z]{0,2}$/.test(value);
}

// ────────────────────────────────────────────────────────────────────────────
// Bornes métier — surface et années
// ────────────────────────────────────────────────────────────────────────────

/** Surface minimale acceptée : 1 m² = 0,01 are (précision NUMERIC(10,2)). */
export const SURFACE_ARES_MIN = 0.01;
/** Surface maximale acceptée : 1000 ha = 100 000 ares. */
export const SURFACE_ARES_MAX = 100_000;

/** Plus ancienne année de plantation acceptée (garde-fou saisie). */
export const ANNEE_PLANTATION_MIN = 1900;

/** Année maximale acceptée : année courante + 1 (plantation prévue été N+1). */
export function getAnneePlantationMax(now: Date = new Date()): number {
  return now.getFullYear() + 1;
}

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques
// ────────────────────────────────────────────────────────────────────────────

/** Statut viticulture — aligné sur CHECK SQL de la table `sous_parcelles_viticoles`. */
export const parcelleStatutSchema = z.enum(["production", "jeune_vigne", "arrachee", "jachere"]);
export type ParcelleStatut = z.infer<typeof parcelleStatutSchema>;

/**
 * Mode de conduite réglementaire AOC Champagne (CdC Chap. I, VI).
 * Donnée CIVC (pas dans CVI Douane).
 */
export const modeConduiteSchema = z.enum(["chablis", "cordon", "guyot", "vallee_marne", "autre"]);
export type ModeConduite = z.infer<typeof modeConduiteSchema>;

/** Couleur de cépage — aligné sur CHECK SQL de la table `cepages`. */
export const couleurCepageSchema = z.enum(["blanc", "noir", "gris", "rose"]);
export type CouleurCepage = z.infer<typeof couleurCepageSchema>;

/**
 * Type de matériel végétal d'une SPCV (Lot 1, migration 0040) — aligné sur
 * CHECK SQL `sous_parcelles_type_materiel_check`.
 *   - `clone`   : clone agréé (référentiel ou saisie libre).
 *   - `massale` : sélection massale (description libre).
 *   - `inconnu` : matériel non tracé (cas par défaut, vieilles vignes).
 */
export const typeMaterielVegetalSchema = z.enum(["clone", "massale", "inconnu"]);
export type TypeMaterielVegetal = z.infer<typeof typeMaterielVegetalSchema>;

/**
 * Technique de greffe greffon ↔ porte-greffe (SPCV, migration 0047) — aligné
 * sur CHECK SQL `sous_parcelles_technique_greffe_check`. Attribut FIGÉ et
 * purement informatif (aucun contrôle de cohérence). NULL/absent = « non
 * renseigné » (cas dominant : technique rarement connue à l'achat en pépinière).
 *   - `greffe_omega`    : greffe oméga (Ω), mécanisée — la plus courante.
 *   - `greffe_anglaise` : greffe à l'anglaise (manuelle à languette).
 *   - `greffe_fente`    : greffe en fente.
 */
export const techniqueGreffeSchema = z.enum(["greffe_omega", "greffe_anglaise", "greffe_fente"]);
export type TechniqueGreffe = z.infer<typeof techniqueGreffeSchema>;

/** Libellés métier des techniques de greffe (UI : sélecteur + affichage). */
export const TECHNIQUE_GREFFE_LABELS: Record<TechniqueGreffe, string> = {
  greffe_omega: "Greffe oméga",
  greffe_anglaise: "Greffe à l'anglaise",
  greffe_fente: "Greffe en fente",
};

export const communeInseeStrictSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => validateCommuneInsee(v), {
    message: "Code commune INSEE invalide (5 caractères, Corse 2A/2B accepté).",
  });

export const sectionCadastraleSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => validateSection(v), {
    message: "Section cadastrale invalide (1 à 4 caractères alphanumériques).",
  });

export const numeroParcelleSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => validateNumero(v), {
    message: "Numéro de parcelle invalide (1 à 4 chiffres, suffixe lettre optionnel).",
  });

export const surfaceAresSchema = z
  .number({
    invalid_type_error: "Surface invalide.",
    required_error: "Surface requise.",
  })
  .positive("La surface doit être supérieure à 0.")
  .min(SURFACE_ARES_MIN, `Surface minimum : ${SURFACE_ARES_MIN} are.`)
  .max(SURFACE_ARES_MAX, `Surface maximum : ${SURFACE_ARES_MAX} ares (1000 ha).`)
  .refine((surface) => Number(surface.toFixed(2)) === surface, {
    message: "Saisissez au maximum deux décimales d’are.",
  });

export const anneePlantationSchema = z
  .number()
  .int()
  .min(ANNEE_PLANTATION_MIN, `Année trop ancienne (≥ ${ANNEE_PLANTATION_MIN}).`)
  .max(getAnneePlantationMax(), `Année trop dans le futur (≤ ${getAnneePlantationMax()}).`);

export const anneeArrachageSchema = z
  .number()
  .int()
  .min(ANNEE_PLANTATION_MIN, `Année trop ancienne (≥ ${ANNEE_PLANTATION_MIN}).`)
  .max(getAnneePlantationMax(), `Année trop dans le futur (≤ ${getAnneePlantationMax()}).`);

/**
 * ID préfixé. 5..32 chars, préfixe 2-5 lettres minuscules + underscore +
 * suffixe alphanumérique (lettres/chiffres/underscore).
 *
 * Deux conventions coexistent : ULID majuscules (entités tenant) +
 * slug minuscules (référentiels seedés type `cep_chardonnay`).
 */
export const idSchema = z
  .string()
  .trim()
  .min(5)
  .max(32)
  .regex(/^[a-z]{2,5}_[A-Za-z0-9_]+$/, "Identifiant invalide.");

// ────────────────────────────────────────────────────────────────────────────
// Polygone GeoJSON
// ────────────────────────────────────────────────────────────────────────────

const positionSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);

export const geoJsonPolygonSchema = z
  .object({
    type: z.literal("Polygon"),
    coordinates: z
      .array(z.array(positionSchema).min(4, "Polygone : minimum 4 positions (triangle fermé)."))
      .length(1, "Polygones à trous non supportés en V0 (un seul anneau extérieur)."),
  })
  .superRefine((value, ctx) => {
    const ring = value.coordinates[0];
    if (!ring) return;
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!first || !last) return;
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Polygone non fermé : la dernière position doit être identique à la première.",
      });
    }
  });
export type GeoJsonPolygon = z.infer<typeof geoJsonPolygonSchema>;

// ────────────────────────────────────────────────────────────────────────────
// PCV (Parcelle Cadastrale Viticole) — cadastre + surface + géométrie + lieu-dit
// ────────────────────────────────────────────────────────────────────────────

const parcelleCadastralePayloadShape = {
  faire_valoir: faireValoirParcelleSchema.nullable().optional(),
  choix_cru: choixCruParcelleSchema.optional(),
  // ----- Cadastre -----
  commune_insee: communeInseeStrictSchema,
  section_cadastrale: sectionCadastraleSchema,
  numero_parcelle: numeroParcelleSchema,
  lieudit: z
    .string()
    .trim()
    .max(120, "Lieu-dit trop long (120 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),

  // ----- Surface cadastrale totale (PCV) -----
  surface_ares: surfaceAresSchema,

  // ----- Géométrie (optionnelle) -----
  polygone: geoJsonPolygonSchema.optional().nullable(),

  // ----- Notes libres -----
  notes: z
    .string()
    .trim()
    .max(4000, "Notes trop longues (4000 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),
} as const;

/**
 * Partie cadastrale de la création. L'endpoint accepte aussi la première
 * SPCV via `parcelleCreationCompleteInput`, enregistrée dans la même transaction.
 */
export const parcelleCreerInput = z.object(parcelleCadastralePayloadShape);
export type ParcelleCreerInput = z.infer<typeof parcelleCreerInput>;

/**
 * Input `parcellaire.modifier` — patch d'une PCV. Tous les champs optionnels
 * sauf `id`.
 */
export const parcelleModifierInput = z
  .object({
    id: idSchema,
    ...parcelleCadastralePayloadShape,
  })
  .partial({
    commune_insee: true,
    section_cadastrale: true,
    numero_parcelle: true,
    lieudit: true,
    surface_ares: true,
    polygone: true,
    notes: true,
  });
export type ParcelleModifierInput = z.infer<typeof parcelleModifierInput>;

/** Input `parcellaire.supprimer` — suppression hard (CASCADE sur SPCV). */
export const parcelleSupprimerInput = z.object({
  id: idSchema,
});
export type ParcelleSupprimerInput = z.infer<typeof parcelleSupprimerInput>;

/** Input `parcellaire.getById` — lecture détail PCV + SPCV imbriquées. */
export const parcelleGetByIdInput = z.object({
  id: idSchema,
  annee_reference: anneeReferenceVignobleSchema.optional(),
});
export type ParcelleGetByIdInput = z.infer<typeof parcelleGetByIdInput>;

/**
 * Input `parcellaire.lister` — filtres optionnels pour la liste tenant.
 * Note : les filtres `statut` et `cepage_id` s'appliquent désormais aux
 * SPCV (une PCV est listée si AU MOINS UNE de ses SPCV matche).
 */
export const parcelleListerInput = z
  .object({
    annee_reference: anneeReferenceVignobleSchema.optional(),
    statut: parcelleStatutSchema.optional(),
    cepage_id: idSchema.optional(),
    cru: cruSchema.optional(),
    recherche_lieudit: z
      .string()
      .trim()
      .max(120)
      .optional()
      .or(z.literal("").transform(() => undefined)),
    inclure_polygone: z.boolean().optional().default(false),
  })
  .optional();
export type ParcelleListerInput = z.infer<typeof parcelleListerInput>;

// ────────────────────────────────────────────────────────────────────────────
// SPCV (Sous-Parcelle Viticole) — agronomie : cépage, dates, conduite
// ────────────────────────────────────────────────────────────────────────────

const sousParcellePayloadShape = {
  /** PCV parente. */
  parcelle_id: idSchema,

  /**
   * Numéro d'ordre SPCV au sein de sa PCV (1, 2, 3…). Doit être unique par
   * parcelle (cf uniqueIndex DB). Si non fourni à la création, le backend
   * attribue MAX(numero_ordre)+1 pour la PCV.
   */
  numero_ordre: z.number().int().min(1).max(99).optional(),

  /** Cépage de la SPCV (mono-cépage par définition agronomique). */
  cepage_id: idSchema,

  /**
   * Surface RÉELLE de la SPCV en ares. La somme des SPCV d'une PCV peut
   * être ≤ à la surface cadastrale de la PCV (allées, jachères, friches…).
   * Invariant applicatif : warning si écart > 5 % à la somme.
   */
  surface_ares: surfaceAresSchema,

  /** Statut viticulture de la SPCV (chaque SPCV évolue indépendamment). */
  statut: parcelleStatutSchema,

  /** Année de plantation de cette SPCV. */
  annee_plantation: anneePlantationSchema.optional(),

  /** Année d'arrachage de cette SPCV. Obligatoire si statut='arrachee'. */
  annee_arrachage: anneeArrachageSchema.optional(),

  /** Mode de conduite réglementaire AOC (Chablis/Cordon/Guyot/Vallée). */
  mode_conduite: modeConduiteSchema.optional(),

  /**
   * Précision libre quand `mode_conduite='autre'` (dérogation, mode hybride).
   * Posé uniquement si `mode_conduite='autre'` (vérifié dans refine).
   * 80 caractères max — synthétique, pas un paragraphe (cf `notes`).
   */
  mode_conduite_precision: z
    .string()
    .trim()
    .max(80, "Précision trop longue (80 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),

  // ----- Matériel végétal (Lot 1, migration 0040) -----

  /**
   * Type de matériel végétal. Défaut 'inconnu' appliqué côté usecase/DB si
   * non fourni (le front l'envoie toujours). La cohérence type ↔ champs est
   * vérifiée dans `refineSousParcelleMaterielVegetal`.
   */
  type_materiel_vegetal: typeMaterielVegetalSchema.optional(),

  /** Clone agréé (référentiel global, ex `clo_95`). Exclusif avec `clone_libre`. */
  clone_id: idSchema.optional(),

  /** Clone hors référentiel (numéro saisi en libre). Exclusif avec `clone_id`. */
  clone_libre: z
    .string()
    .trim()
    .max(60, "Clone libre trop long (60 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),

  /** Description de la sélection massale (origine des pieds, parcelle mère…). */
  massale_description: z
    .string()
    .trim()
    .max(120, "Description massale trop longue (120 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),

  /** Porte-greffe (référentiel global, ex `pg_41b`). Exclusif avec `porte_greffe_libre`. */
  porte_greffe_id: idSchema.optional(),

  /** Porte-greffe hors référentiel (saisie libre). Exclusif avec `porte_greffe_id`. */
  porte_greffe_libre: z
    .string()
    .trim()
    .max(40, "Porte-greffe libre trop long (40 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),

  /**
   * Technique de greffe (migration 0047). Indépendante des autres champs
   * (aucun refine). `null` efface explicitement (PATCH), `undefined` = ne pas
   * toucher. La chaîne vide « non renseigné » du <select> front est convertie
   * en `null` au moment du payload.
   */
  technique_greffe: techniqueGreffeSchema
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),

  /** Notes libres SPCV. */
  notes: z
    .string()
    .trim()
    .max(4000, "Notes trop longues (4000 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),
} as const;

/**
 * Identifiant du cépage Meunier au référentiel (seedé en migration 0028). Seul
 * cépage autorisé pour la taille « Vallée de la Marne » (CdC AOC Champagne).
 */
export const CEPAGE_MEUNIER_ID = "cep_meunier";

/**
 * Message métier UNIQUE de l'invariant « taille Vallée de la Marne ⇒ Meunier ».
 * Partagé entre le refine Zod (création) et l'erreur du use case (modification)
 * pour rester strictement cohérent (DRY).
 */
export const TAILLE_VALLEE_MARNE_MEUNIER_MESSAGE =
  "La taille « Vallée de la Marne » est réservée au cépage Meunier (CdC AOC Champagne Chap. I, Section VI).";

/**
 * Invariant CdC AOC Champagne, Chap. I, Section VI (« Taille de la Vallée de la
 * Marne ») : cette taille (`mode_conduite='vallee_marne'`) n'est autorisée QUE
 * pour le cépage Meunier N. Source : arrêté 25/01/2024 (JORFTEXT000049041513).
 *
 * Fonction PURE partagée, rejouée à la CRÉATION (refine Zod, cépage toujours
 * présent) ET en MODIFICATION partielle (use case, sur les valeurs FUSIONNÉES
 * `patch ?? courant`). Sans ce rejeu, un PATCH ne renvoyant qu'un seul des deux
 * champs (le mode seul, ou le cépage seul) contournerait le contrôle.
 *
 * @returns `true` si la combinaison VIOLE la règle (mode « Vallée de la Marne »
 *          + cépage connu ≠ Meunier). `false` si conforme, ou si le cépage est
 *          indéterminé (`null`/`undefined`) — on ne peut alors pas trancher.
 */
export function violeTailleValleeMarneReserveeMeunier(params: {
  cepageId: string | null | undefined;
  modeConduite: string | null | undefined;
}): boolean {
  return (
    params.modeConduite === "vallee_marne" &&
    params.cepageId !== null &&
    params.cepageId !== undefined &&
    params.cepageId !== CEPAGE_MEUNIER_ID
  );
}

/**
 * Cohérence statut ↔ années (arrachage requiert statut='arrachee' et
 * annee_arrachage ≥ annee_plantation).
 */
function refineSousParcelleCore(
  value: {
    statut?: ParcelleStatut;
    annee_plantation?: number | null;
    annee_arrachage?: number;
    cepage_id?: string;
    mode_conduite?: string;
    mode_conduite_precision?: string;
  },
  ctx: z.RefinementCtx,
): void {
  // Précision libre uniquement permise si mode='autre' — sinon on refuse
  // (sinon on aurait par ex. `Guyot + précision="Cordon hybride"` qui n'a
  // aucun sens et casserait l'agrégation par mode).
  if (value.mode_conduite_precision && value.mode_conduite !== "autre") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["mode_conduite_precision"],
      message: "La précision n'est utilisable qu'avec mode de conduite « Autre ».",
    });
  }
  // CdC AOC Champagne, Chap. I, Section VI « Taille de la Vallée de la Marne » :
  // règle centralisée dans `violeTailleValleeMarneReserveeMeunier` (fonction pure
  // partagée avec le use case de modification, DRY). Ici cépage toujours présent.
  if (
    violeTailleValleeMarneReserveeMeunier({
      cepageId: value.cepage_id,
      modeConduite: value.mode_conduite,
    })
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["mode_conduite"],
      message: TAILLE_VALLEE_MARNE_MEUNIER_MESSAGE,
    });
  }
  if (value.statut === "arrachee" && value.annee_arrachage === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["annee_arrachage"],
      message: "Année d'arrachage requise pour une SPCV arrachée.",
    });
  }
  if (value.statut && value.statut !== "arrachee" && value.annee_arrachage !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["annee_arrachage"],
      message: "Année d'arrachage uniquement pour les SPCV arrachées.",
    });
  }
  if (
    value.annee_arrachage !== undefined &&
    value.annee_plantation !== undefined &&
    value.annee_plantation !== null &&
    value.annee_arrachage < value.annee_plantation
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["annee_arrachage"],
      message: "L'année d'arrachage doit être ≥ à l'année de plantation.",
    });
  }
}

/**
 * Cohérence matériel végétal (Lot 1) — reproduit les CHECK DB de la migration
 * 0040 (`sous_parcelles_materiel_coherence_check`, `_clone_xor_check`,
 * `_porte_greffe_xor_check`) pour un message d'erreur propre côté formulaire.
 *
 * La validation type ↔ champs ne s'applique que si `type_materiel_vegetal` est
 * défini (le front l'envoie toujours ; en patch partiel sans ce champ, on ne
 * peut pas trancher la cohérence). Les XOR clone/porte-greffe sont vérifiés
 * inconditionnellement (jamais les deux saisies ensemble).
 */
function refineSousParcelleMaterielVegetal(
  value: {
    type_materiel_vegetal?: TypeMaterielVegetal;
    clone_id?: string;
    clone_libre?: string;
    massale_description?: string;
    porte_greffe_id?: string;
    porte_greffe_libre?: string;
  },
  ctx: z.RefinementCtx,
): void {
  const aClone = value.clone_id !== undefined || value.clone_libre !== undefined;
  const aMassale = value.massale_description !== undefined;

  // XOR clone : jamais référentiel ET saisie libre en même temps.
  if (value.clone_id !== undefined && value.clone_libre !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["clone_libre"],
      message: "Choisir un clone du référentiel OU une saisie libre, pas les deux.",
    });
  }
  // XOR porte-greffe : idem.
  if (value.porte_greffe_id !== undefined && value.porte_greffe_libre !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["porte_greffe_libre"],
      message: "Choisir un porte-greffe du référentiel OU une saisie libre, pas les deux.",
    });
  }

  // Cohérence type ↔ champs : uniquement si le type est fourni.
  if (value.type_materiel_vegetal === undefined) return;

  if (value.type_materiel_vegetal === "clone") {
    if (!aClone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["clone_id"],
        message:
          "Renseigner un clone (référentiel ou saisie libre) pour un matériel de type clone.",
      });
    }
    if (aMassale) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["massale_description"],
        message: "Une description de massale n'est pas permise avec un matériel de type clone.",
      });
    }
  } else if (value.type_materiel_vegetal === "massale") {
    if (!aMassale) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["massale_description"],
        message: "Renseigner une description pour une sélection massale.",
      });
    }
    if (aClone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["clone_id"],
        message: "Un clone n'est pas permis avec un matériel de type sélection massale.",
      });
    }
  } else {
    // type_materiel_vegetal === "inconnu" : aucun détail de matériel autorisé.
    if (aClone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["clone_id"],
        message: "Aucun clone n'est permis quand le matériel végétal est inconnu.",
      });
    }
    if (aMassale) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["massale_description"],
        message:
          "Aucune description de massale n'est permise quand le matériel végétal est inconnu.",
      });
    }
  }
}

/** Applique successivement les deux refines (statut/années + matériel végétal). */
function refineSousParcelle(value: unknown, ctx: z.RefinementCtx): void {
  refineSousParcelleCore(value as Parameters<typeof refineSousParcelleCore>[0], ctx);
  refineSousParcelleMaterielVegetal(
    value as Parameters<typeof refineSousParcelleMaterielVegetal>[0],
    ctx,
  );
}

/** Input `parcellaire.creerSousParcelle` — créer une SPCV dans une PCV existante. */
export const sousParcelleCreerInput = z
  .object(sousParcellePayloadShape)
  .superRefine(refineSousParcelle);
export type SousParcelleCreerInput = z.infer<typeof sousParcelleCreerInput>;

/** Même validation agronomique ; le parent est attribué exclusivement par le serveur. */
export const premiereSousParcelleInput = z
  .object(sousParcellePayloadShape)
  .omit({ parcelle_id: true })
  .strict()
  .superRefine(refineSousParcelle);

/** Créer les deux objets ensemble ou aucun ; les anciennes créations PCV seules restent admises. */
export const parcelleCreationCompleteInput = parcelleCreerInput.extend({
  premiere_sous_parcelle: premiereSousParcelleInput.optional(),
});
export type ParcelleCreationCompleteInput = z.infer<typeof parcelleCreationCompleteInput>;

/** Input `parcellaire.modifierSousParcelle` — patch d'une SPCV existante. */
export const sousParcelleModifierInput = z
  .object({
    id: idSchema,
    ...sousParcellePayloadShape,
    annee_plantation: anneePlantationSchema.nullable().optional(),
  })
  .partial({
    parcelle_id: true,
    numero_ordre: true,
    cepage_id: true,
    surface_ares: true,
    statut: true,
    annee_plantation: true,
    annee_arrachage: true,
    mode_conduite: true,
    mode_conduite_precision: true,
    type_materiel_vegetal: true,
    clone_id: true,
    clone_libre: true,
    massale_description: true,
    porte_greffe_id: true,
    porte_greffe_libre: true,
    technique_greffe: true,
    notes: true,
  })
  .superRefine(refineSousParcelle);
export type SousParcelleModifierInput = z.infer<typeof sousParcelleModifierInput>;

/** Input `parcellaire.supprimerSousParcelle` — suppression d'une SPCV. */
export const sousParcelleSupprimerInput = z.object({
  id: idSchema,
});
export type SousParcelleSupprimerInput = z.infer<typeof sousParcelleSupprimerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Référentiels matériel végétal (Lot 1)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `parcellaire.listerClones` — liste du référentiel clones.
 * `cepage_id` optionnel : si fourni, filtre les clones de ce cépage (cas UI
 * du formulaire SPCV) ; sinon retourne tout le référentiel.
 */
export const listerClonesInput = z
  .object({
    cepage_id: idSchema.optional(),
  })
  .optional();
export type ListerClonesInput = z.infer<typeof listerClonesInput>;
